import { X } from "lucide-react";

interface CashAmountInputProps {
  value: string;
  displayValue: string;
  onChange: (numericValue: string, displayValue: string) => void;
  locale?: string;
  currency?: string;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
}

export function CashAmountInput({
  value,
  displayValue,
  onChange,
  locale = "pt-BR",
  currency = "BRL",
  placeholder = "0,00",
  label,
  className = "",
  disabled = false,
  inputMode = "decimal",
  onFocus,
}: CashAmountInputProps) {
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    
    // Remove all non-digit characters
    const digitsOnly = inputValue.replace(/\D/g, "");
    
    // If empty, reset
    if (!digitsOnly) {
      onChange("", "");
      return;
    }
    
    // Convert to number (treating last 2 digits as decimals)
    const numericValue = parseInt(digitsOnly, 10) / 100;
    
    // Format to country-specific currency style
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
    
    onChange(numericValue.toString(), formatted);
  };

  const handleClear = () => {
    onChange("", "");
  };

  // Get currency symbol based on locale and currency
  const getCurrencySymbol = () => {
    if (currency === "BRL") return "R$";
    if (currency === "USD") return "$";
    if (currency === "EUR") return "€";
    return currency;
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
          {getCurrencySymbol()}
        </span>
        <input
          type="text"
          inputMode={inputMode}
          autoComplete="off"
          enterKeyHint="done"
          value={displayValue}
          onChange={handleAmountChange}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-12 pr-12 py-3 bg-white dark:bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {displayValue && !disabled && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-muted rounded-full transition-colors"
            aria-label="Limpar valor"
            type="button"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  );
}
